export const memoize = <
  In extends WeakKey,
  Out extends NonNullable<unknown> | null,
>(
  fn: (input: In) => Out,
): ((input: In) => Out) => {
  const memo = new WeakMap<In, Out>();
  return (input: In) => {
    const memoized = memo.get(input);
    if (memoized !== undefined) {
      return memoized;
    }
    const calculated = fn(input);
    memo.set(input, calculated);
    return calculated;
  };
};
/**
 * memoize2 memoize a two parameter function using WeakMap.
 * Note: uses second argument for outer map, while memoize and memoize{3,4,5} memoize in order of arguments
 * This is due to all call sites of memoize2 currently having their first argument more likely to change than the other
 * And if the other changes, then probably all old values can be garbage collected at that point
 * @param fn function to memoize
 */

export const memoize2 = <
  In1 extends WeakKey,
  In2 extends WeakKey,
  Out extends NonNullable<unknown> | null,
>(
  fn: (input1: In1, input2: In2) => Out,
): ((input1: In1, input2: In2) => Out) => {
  const memo2 = new WeakMap<In2, WeakMap<In1, Out>>();
  return (input1: In1, input2: In2) => {
    let memo = memo2.get(input2);
    if (!memo) {
      memo = new WeakMap<In1, Out>();
      memo2.set(input2, memo);
    }
    const memoized = memo.get(input1);
    if (memoized !== undefined) {
      return memoized;
    }
    const calculated = fn(input1, input2);
    memo.set(input1, calculated);
    return calculated;
  };
};

export const memoize3 = <
  In1 extends WeakKey,
  In2 extends WeakKey,
  In3 extends WeakKey,
  Out extends NonNullable<unknown> | null,
>(
  fn: (input1: In1, input2: In2, input3: In3) => Out,
): ((input1: In1, input2: In2, input3: In3) => Out) => {
  const memo1 = new WeakMap<In1, WeakMap<In2, WeakMap<In3, Out>>>();
  return (input1: In1, input2: In2, input3: In3) => {
    let memo2 = memo1.get(input1);
    if (!memo2) {
      memo2 = new WeakMap<In2, WeakMap<In3, Out>>();
      memo1.set(input1, memo2);
    }
    let memo3 = memo2.get(input2);
    if (!memo3) {
      memo3 = new WeakMap<In3, Out>();
      memo2.set(input2, memo3);
    }
    const memoized = memo3.get(input3);
    if (memoized !== undefined) {
      return memoized;
    }
    const calculated = fn(input1, input2, input3);
    memo3.set(input3, calculated);
    return calculated;
  };
};

export const memoize4 = <
  In1 extends WeakKey,
  In2 extends WeakKey,
  In3 extends WeakKey,
  In4 extends WeakKey,
  Out extends NonNullable<unknown> | null,
>(
  fn: (input1: In1, input2: In2, input3: In3, input4: In4) => Out,
): ((input1: In1, input2: In2, input3: In3, input4: In4) => Out) => {
  const memo1 = new WeakMap<
    In1,
    WeakMap<In2, WeakMap<In3, WeakMap<In4, Out>>>
  >();
  return (input1: In1, input2: In2, input3: In3, input4: In4) => {
    let memo2 = memo1.get(input1);
    if (!memo2) {
      memo2 = new WeakMap<In2, WeakMap<In3, WeakMap<In4, Out>>>();
      memo1.set(input1, memo2);
    }
    let memo3 = memo2.get(input2);
    if (!memo3) {
      memo3 = new WeakMap<In3, WeakMap<In4, Out>>();
      memo2.set(input2, memo3);
    }
    let memo4 = memo3.get(input3);
    if (!memo4) {
      memo4 = new WeakMap<In4, Out>();
      memo3.set(input3, memo4);
    }
    const memoized = memo4.get(input4);
    if (memoized !== undefined) {
      return memoized;
    }
    const calculated = fn(input1, input2, input3, input4);
    memo4.set(input4, calculated);
    return calculated;
  };
};

export const memoize5 = <
  In1 extends WeakKey,
  In2 extends WeakKey,
  In3 extends WeakKey,
  In4 extends WeakKey,
  In5 extends WeakKey,
  Out extends NonNullable<unknown> | null,
>(
  fn: (input1: In1, input2: In2, input3: In3, input4: In4, input5: In5) => Out,
): ((
  input1: In1,
  input2: In2,
  input3: In3,
  input4: In4,
  input5: In5,
) => Out) => {
  const memo1 = new WeakMap<
    In1,
    WeakMap<In2, WeakMap<In3, WeakMap<In4, WeakMap<In5, Out>>>>
  >();
  return (input1: In1, input2: In2, input3: In3, input4: In4, input5: In5) => {
    let memo2 = memo1.get(input1);
    if (!memo2) {
      memo2 = new WeakMap<In2, WeakMap<In3, WeakMap<In4, WeakMap<In5, Out>>>>();
      memo1.set(input1, memo2);
    }
    let memo3 = memo2.get(input2);
    if (!memo3) {
      memo3 = new WeakMap<In3, WeakMap<In4, WeakMap<In5, Out>>>();
      memo2.set(input2, memo3);
    }
    let memo4 = memo3.get(input3);
    if (!memo4) {
      memo4 = new WeakMap<In4, WeakMap<In5, Out>>();
      memo3.set(input3, memo4);
    }
    let memo5 = memo4.get(input4);
    if (!memo5) {
      memo5 = new WeakMap<In5, Out>();
      memo4.set(input4, memo5);
    }
    const memoized = memo5.get(input5);
    if (memoized !== undefined) {
      return memoized;
    }
    const calculated = fn(input1, input2, input3, input4, input5);
    memo5.set(input5, calculated);
    return calculated;
  };
};

type NonWeakKey = string | number | null;
type FlexKey = WeakKey | NonWeakKey;

type Node<V> = {
  weak?: WeakMap<WeakKey, Node<V> | V>;
  strong?: Map<NonWeakKey, Node<V> | V>;
};

const isWeakKey = (k: FlexKey): k is WeakKey =>
  typeof k === 'object' && k !== null;

const ensureMap = <V>(node: Node<V>, key: FlexKey) =>
  isWeakKey(key)
    ? node.weak || (node.weak = new WeakMap<WeakKey, Node<V> | V>())
    : node.strong || (node.strong = new Map<NonWeakKey, Node<V> | V>());

/** Memoizes a function with a variable number of arguments.
 * Each argument can be either WeakKey or string | null.
 * Less efficient than the numerated WeakKey variants above so prefer those where possible. */
export function memoizeN<Args extends readonly FlexKey[], Out>(
  fn: (...args: Args) => Out,
): (...args: Args) => Out {
  const root: Node<Out> = {};

  return (...args: Args): Out => {
    if (args.length === 0)
      throw new Error('memoizeN requires at least one argument');

    let node = root;

    // Walk/create intermediate nodes
    for (let i = 0; i < args.length - 1; i++) {
      const key = args[i] as FlexKey;
      const map = ensureMap(node, key) as Map<FlexKey, Node<Out>>; // type warning: `map` may be a WeakMap, but only if key is also compatible
      let next = map.get(key);
      if (!next) {
        next = {};
        map.set(key, next);
      }
      node = next;
    }

    // Leaf: same pattern, but store/retrieve Out
    const last = args[args.length - 1] as FlexKey;
    const leaf = ensureMap(node, last) as Map<FlexKey, Out>; // type warning: `leaf` may be a WeakMap, but is selected appropriately for `last`
    const hit = leaf.get(last);
    if (hit !== undefined) return hit;

    const val = fn(...args);
    leaf.set(last, val);
    return val;
  };
}

export const nonWeakMemoize2 = <
  In1,
  In2,
  Out extends NonNullable<unknown> | null,
>(
  fn: (input1?: In1, input2?: In2) => Out,
): ((input1?: In1, input2?: In2) => Out) => {
  const memo1 = new Map<In1 | undefined, Map<In2 | undefined, Out>>();
  return (input1: In1 | undefined, input2: In2 | undefined) => {
    let memo2 = memo1.get(input1);
    if (!memo2) {
      memo2 = new Map<In2 | undefined, Out>();
      memo1.set(input1, memo2);
    }
    const memoized = memo2.get(input2);
    if (memoized !== undefined) {
      return memoized;
    }
    const calculated = fn(input1, input2);
    memo2.set(input2, calculated);
    return calculated;
  };
};

export const nonWeakMemoize3 = <
  In1,
  In2,
  In3,
  Out extends NonNullable<unknown> | null,
>(
  fn: (input1?: In1, input2?: In2, input3?: In3) => Out,
): ((input1?: In1, input2?: In2, input3?: In3) => Out) => {
  const memo1 = new Map<
    In1 | undefined,
    Map<In2 | undefined, Map<In3 | undefined, Out>>
  >();
  return (
    input1: In1 | undefined,
    input2: In2 | undefined,
    input3: In3 | undefined,
  ) => {
    let memo2 = memo1.get(input1);
    if (!memo2) {
      memo2 = new Map<In2 | undefined, Map<In3 | undefined, Out>>();
      memo1.set(input1, memo2);
    }
    let memo3 = memo2.get(input2);
    if (!memo3) {
      memo3 = new Map<In3 | undefined, Out>();
      memo2.set(input2, memo3);
    }
    const memoized = memo3.get(input3);
    if (memoized !== undefined) {
      return memoized;
    }
    const calculated = fn(input1, input2, input3);
    memo3.set(input3, calculated);
    return calculated;
  };
};

/** Returns a memoized Set from an array. Useful for efficient ad-hoc lookups from an array. */
export const getMemoSet = memoize(<T>(xs: readonly T[]) => new Set(xs));
